import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { Zap, Menu, X } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // We hide the Tutorials link from nav until at least one tutorial is
  // published. Single endpoint call on mount; cheap and avoids prop-drilling.
  const [hasTutorials, setHasTutorials] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/tutorials`)
      .then((res) => {
        if (!cancelled) setHasTutorials((res.data?.count || 0) > 0);
      })
      .catch(() => { /* silently keep hidden */ });
    return () => { cancelled = true; };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/account');
    setMobileMenuOpen(false);
  };

  const navItems = [
    { label: 'Generate', path: '/' },
    { label: 'My Gallery', path: '/my-gallery' },
    ...(hasTutorials ? [{ label: 'Tutorials', path: '/tutorials' }] : []),
    { label: 'Credits', path: '/credits' },
    { label: 'Account', path: '/account' }
  ];

  const handleNav = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#0F1419]">
      {/* Header */}
      <header className="border-b border-[#1A2035] sticky top-0 z-50 bg-[#0F1419]/95 backdrop-blur-sm">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-2 sm:gap-3 cursor-pointer" onClick={() => handleNav('/')} data-testid="logo">
              <img src="/favicon.svg" alt="Visual Atmos" className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg" />
              <span className="text-lg sm:text-xl font-semibold text-white">Visual Atmos</span>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              {navItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => handleNav(item.path)}
                  className={`text-sm font-medium transition-colors ${
                    location.pathname === item.path
                      ? 'text-[#FFB238]'
                      : 'text-gray-300 hover:text-white'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  {item.label}
                </button>
              ))}
            </nav>

            {/* Desktop User Section */}
            <div className="hidden md:flex items-center gap-4">
              {user ? (
                <>
                  <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1A2035] border border-[#222D4A]" data-testid="credits-display">
                    <Zap className="w-4 h-4 text-[#FFB238]" fill="#FFB238" />
                    <span className="text-sm font-medium text-white">{user.credits_balance?.toLocaleString()} credits</span>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="text-sm text-gray-300 hover:text-white transition-colors"
                    data-testid="logout-button"
                  >
                    Log Out
                  </button>
                </>
              ) : (
                <button
                  onClick={() => handleNav('/account')}
                  className="px-4 py-2 rounded-lg bg-[#FFB238] text-[#0F1419] font-medium hover:bg-[#FFDA82] transition-colors"
                  data-testid="login-button"
                >
                  Sign In
                </button>
              )}
            </div>

            {/* Mobile: credits + hamburger */}
            <div className="flex md:hidden items-center gap-3">
              {user && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1A2035] border border-[#222D4A]" data-testid="credits-display-mobile">
                  <Zap className="w-3.5 h-3.5 text-[#FFB238]" fill="#FFB238" />
                  <span className="text-xs font-medium text-white">{user.credits_balance?.toLocaleString()}</span>
                </div>
              )}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-gray-300 hover:text-white"
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-[#1A2035] bg-[#0F1419]">
            <div className="px-4 py-3 space-y-1">
              {navItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => handleNav(item.path)}
                  className={`block w-full text-left px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === item.path
                      ? 'text-[#FFB238] bg-[#FFB238]/10'
                      : 'text-gray-300 hover:text-white hover:bg-[#1A2035]'
                  }`}
                >
                  {item.label}
                </button>
              ))}
              <div className="pt-2 border-t border-[#1A2035]">
                {user ? (
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-4 py-3 rounded-lg text-sm font-medium text-gray-400 hover:text-white hover:bg-[#1A2035]"
                  >
                    Log Out
                  </button>
                ) : (
                  <button
                    onClick={() => handleNav('/account')}
                    className="block w-full text-center px-4 py-3 rounded-lg bg-[#FFB238] text-[#0F1419] font-bold text-sm"
                  >
                    Sign In
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="min-h-[calc(100vh-73px)]">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-[#1A2035] bg-[#0B0F14]">
        <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex flex-col items-center gap-4 text-center">
            <p className="text-gray-500 text-xs sm:text-sm">&copy; {new Date().getFullYear()} Visual Atmos. All rights reserved.</p>
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
              {hasTutorials && (
                <Link to="/tutorials" className="text-gray-500 text-xs sm:text-sm hover:text-gray-300 transition-colors">Tutorials</Link>
              )}
              <Link to="/terms" className="text-gray-500 text-xs sm:text-sm hover:text-gray-300 transition-colors">Terms & Conditions</Link>
              <Link to="/privacy" className="text-gray-500 text-xs sm:text-sm hover:text-gray-300 transition-colors">Privacy Policy</Link>
            </div>
            <p className="text-gray-700 text-[10px] leading-relaxed max-w-4xl">
              Visual Atmos is an independent AI-assisted media generation platform. We are not affiliated, associated, authorized, endorsed by, or in any way officially connected with Disney, Marvel, Warner Bros, Nintendo, or any of their subsidiaries or affiliates. All product and company names are trademarks&trade; or registered&reg; trademarks of their respective holders. Use of them does not imply any affiliation with or endorsement by them.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Layout;
